import { Outlet, ScrollRestoration, useLoaderData, useLocation, useMatches } from "react-router";
import type { LoaderFunctionArgs } from "react-router";
import { useEffect } from "react";
import { donateActionJsonLd, organizationJsonLd, webPageJsonLd, websiteJsonLd } from "@/lib/jsonld";

import { fetchSiteContent, type SiteContent } from "./content";
import { useDocumentMeta } from "./use-document-meta";
import { ContentProvider } from "./content-context";
import { isPageData } from "./page-data";

import { Footer } from "@/components/layout/Footer";
import { Header } from "@/components/layout/Header";
import { MotionProvider } from "@/components/layout/MotionProvider";
import { JsonLd } from "@/components/ui/JsonLd";

/**
 * The shell every route renders inside. This is what layout.tsx used to be.
 *
 * The organisation and website JSON-LD are emitted once here rather than per
 * page, so the graph has exactly one node for each and the @id references from
 * breadcrumbs and articles always resolve.
 */
/**
 * One content fetch per render, at the top of the tree.
 *
 * On the server the SSR handler has already loaded this and passes it through
 * the router context, so a page render never makes a second round trip to the
 * API. In the browser after a client-side navigation there is no context, so
 * the loader fetches through the /api proxy instead.
 */
export async function rootLoader({ context, request }: LoaderFunctionArgs): Promise<SiteContent> {
  const provided = (context as { content?: SiteContent } | undefined)?.content;
  return provided ?? fetchSiteContent(request.signal);
}

export function Root() {
  const content = useLoaderData<SiteContent>();
  const location = useLocation();
  const matches = useMatches();
  useDocumentMeta(content);

  useEffect(() => {
    if (!location.hash) return;
    const id = decodeURIComponent(location.hash.slice(1));
    let attempts = 0;
    let timer = 0;

    const moveToAnchor = () => {
      const target = document.getElementById(id);
      if (!target && attempts < 8) {
        attempts += 1;
        timer = window.setTimeout(moveToAnchor, 50);
        return;
      }
      if (!target) return;

      // ScrollRestoration correctly handles route changes, but may reset a
      // direct hash navigation after the browser's first native jump. Repeat
      // the anchor positioning after render, without a slow smooth-scroll that
      // can leave the target several screens away while the user starts reading.
      const previous = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = "auto";
      target.scrollIntoView({ block: "start" });
      document.documentElement.style.scrollBehavior = previous;
    };

    timer = window.setTimeout(moveToAnchor, 0);
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.hash]);

  /*
   * The graph is built from the live settings document, not from a constant.
   *
   * One node for the organisation and one for the website, emitted here rather
   * than per page, so the @id references from breadcrumbs and articles always
   * resolve. DonateAction is emitted only when a verified payment destination
   * exists, which today it does not, so it is null and nothing is written.
   */
  const settings = content.settings ?? undefined;
  const donate = donateActionJsonLd(settings);
  // The page record travels with the page's own route, not in the bootstrap.
  const page = matches.map((match) => match.data).filter(isPageData).at(-1)?.page ?? null;
  const pageSchema = page
    ? webPageJsonLd({
        title: page.headline || page.title,
        description: page.metaDescription,
        path: location.pathname,
        type: page.schemaType,
        settings,
      })
    : null;

  /*
   * Brand colours, from the settings document.
   *
   * This is what makes the platform re-brandable without a code change. The
   * three values are validated as six-digit hex by the API before they are
   * stored, and they are checked again here, so the only thing that can reach
   * the stylesheet is a colour. A token field that accepted arbitrary CSS would
   * be a style injection with the organisation's own admin panel as the vector.
   *
   * The tokens are only overridden when they differ from the defaults, so the
   * common case emits nothing at all.
   */
  const themeOverrides = brandOverrides(settings?.theme);

  return (
    <>
      {/*
        Without JavaScript, Framer Motion never runs its enter animations, so
        any element it staged would stay at opacity 0. Force final states.
      */}
      <noscript>
        <style>{`[style*="opacity:0"],[style*="opacity: 0"]{opacity:1!important;transform:none!important}`}</style>
      </noscript>

      {themeOverrides ? <style>{themeOverrides}</style> : null}

      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-[10px] focus:bg-navy focus:px-5 focus:py-3 focus:text-white"
      >
        Skip to main content
      </a>

      <ContentProvider value={content}>
        <MotionProvider>
          <Header />
          <main id="main">
            <Outlet />
          </main>
          <Footer />
        </MotionProvider>
      </ContentProvider>

      <JsonLd data={organizationJsonLd(settings)} />
      <JsonLd data={websiteJsonLd(settings)} />
      {pageSchema ? <JsonLd data={pageSchema} /> : null}
      {donate ? <JsonLd data={donate} /> : null}

      {/*
        Restores scroll on back and forward, and sends a new page to the top.
        The browser does this for a document navigation and not for a client
        one, so without it the second page a visitor opens starts halfway down.
      */}
      <ScrollRestoration />
    </>
  );
}

/**
 * Turns the three brand colours into a token override, or nothing.
 *
 * Anything that is not a six-digit hex value is dropped rather than escaped:
 * there is no legitimate colour this rejects, and no string that gets through
 * it can close a declaration.
 */
const HEX = /^#[0-9a-fA-F]{6}$/;

function brandOverrides(theme?: { primary?: string; secondary?: string; accent?: string }): string | null {
  if (!theme) return null;
  const defaults: Record<string, string> = {
    "--color-navy": "#14386f",
    "--color-blue": "#2f6f9f",
    "--color-sky": "#b9ddeb",
  };
  const wanted: Record<string, string | undefined> = {
    "--color-navy": theme.primary,
    "--color-blue": theme.secondary,
    "--color-sky": theme.accent,
  };

  const declarations = Object.entries(wanted)
    .filter(([token, value]) => value && HEX.test(value) && value.toLowerCase() !== defaults[token])
    .map(([token, value]) => `${token}:${value};`);

  return declarations.length ? `:root{${declarations.join("")}}` : null;
}
